// Modal for opening check-in and sending join links to registered attendees
import { useEffect, useMemo, useState, type FormEvent, type ChangeEvent } from 'react'
import { Mail, Send, XCircle, QrCode } from 'lucide-react'

import { CardContent, CardHeader, CardTitle, CardDescription } from '@/ui/card'
import { Button } from '@/ui/button'
import { Modal } from '@/ui/modal'
import { useClassSessionStore, type ClassSession, type OpenCheckinPayload, type SessionAttendee, type Student } from '@/features/classes/stores'
import { getAllLocations, type Location } from '@/features/classes/api/locationsService'
import { useToast } from '@/core/hooks/useToast'

import { inputClass } from '../utils/formatters'

interface OpenCheckinModalProps {
  session: ClassSession
  onClose: () => void
  onConfirm: (payload: OpenCheckinPayload) => Promise<void>
}

interface AttendeeOverrideState {
  email: string
  include: boolean
}

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function OpenCheckinModal({ session, onClose, onConfirm }: OpenCheckinModalProps) {
  const rawAttendees = useClassSessionStore((s) => s.attendees)
  const rawStudents = useClassSessionStore((s) => s.students)
  const loadSessionAttendees = useClassSessionStore((s) => s.loadSessionAttendees)
  const attendees = useMemo((): Array<SessionAttendee & { student: Student }> =>
    rawAttendees
      .filter((a) => a.sessionId === session.id)
      .map((a) => ({ ...a, student: rawStudents.find((st) => st.id === a.studentId)! }))
      .filter((a) => a.student),
    [rawAttendees, rawStudents, session.id]
  )
  const sendableAttendees = useMemo(
    () => attendees.filter((a) => a.status === 'registered' || a.status === 'paid'),
    [attendees]
  )

  const [locationId, setLocationId] = useState(session.locationId ?? '')
  const [locations, setLocations] = useState<Location[]>([])
  const [note, setNote] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')
  const [overrides, setOverrides] = useState<Record<string, AttendeeOverrideState>>({})

  const { show, ToastUI } = useToast()

  useEffect(() => {
    loadSessionAttendees(session.id).catch(() => {
      // Error already logged by the attendee store
    })
  }, [loadSessionAttendees, session.id])

  useEffect(() => {
    getAllLocations()
      .then((loaded) => {
        setLocations(loaded)
        if (!locationId && loaded.length > 0) {
          setLocationId(loaded[0].id)
        }
      })
      .catch(() => {
        // Locations are required; leave the select empty and let the form validate.
      })
  }, [locationId])

  useEffect(() => {
    const initial: Record<string, AttendeeOverrideState> = {}
    for (const attendee of sendableAttendees) {
      initial[attendee.id] = {
        email: attendee.student.email ?? '',
        include: true,
      }
    }
    setOverrides(initial)
  }, [sendableAttendees])

  const joinUrl = useMemo(
    () => `${window.location.origin}/join/${session.qrToken}`,
    [session.qrToken]
  )

  const includedAttendees = useMemo(
    () => sendableAttendees.filter((a) => overrides[a.id]?.include),
    [sendableAttendees, overrides]
  )

  const invalidEmailIds = useMemo(
    () =>
      includedAttendees
        .filter((a) => {
          const email = overrides[a.id]?.email.trim() ?? ''
          return !email || !emailRegex.test(email)
        })
        .map((a) => a.id),
    [includedAttendees, overrides]
  )

  const canSend = locationId.trim() !== '' && invalidEmailIds.length === 0

  function updateAttendeeEmail(attendeeId: string, email: string) {
    setOverrides((prev) => ({
      ...prev,
      [attendeeId]: { ...prev[attendeeId], email },
    }))
  }

  function toggleAttendeeInclude(attendeeId: string, include: boolean) {
    setOverrides((prev) => ({
      ...prev,
      [attendeeId]: { ...prev[attendeeId], include },
    }))
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (invalidEmailIds.length > 0) {
      setError('Please fix invalid email addresses before sending.')
      return
    }
    const trimmedNote = note.trim()
    if (trimmedNote.length > 1000) {
      setError('Note must be 1000 characters or fewer.')
      return
    }

    setSending(true)
    setError('')
    try {
      const payload: OpenCheckinPayload = {
        attendeeOverrides: sendableAttendees.map((attendee) => ({
          attendeeId: attendee.id,
          email: overrides[attendee.id]?.email.trim(),
          include: overrides[attendee.id]?.include ?? false,
        })),
      }
      if (trimmedNote) {
        payload.note = trimmedNote
      }
      if (locationId && locationId !== session.locationId) {
        payload.locationId = locationId
      }
      await onConfirm(payload)
      show('Check-in opened and notifications sent.', 'success')
      onClose()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not open check-in. Try again.'
      setError(message)
      show(message, 'error')
      setSending(false)
    }
  }

  return (
    <Modal open onClose={onClose} className="max-w-2xl p-0 max-h-[90vh] overflow-y-auto">
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-navy flex items-center gap-2">
          <QrCode className="w-4 h-4 text-primary" />
          Open Check-in
        </CardTitle>
        <CardDescription>
          Send the join link to registered attendees and update the session location if needed.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              <XCircle className="w-4 h-4 shrink-0" />
              {error}
            </div>
          )}

          <div>
            <label htmlFor="open-checkin-location" className="block text-sm font-medium text-navy mb-1">
              Location
            </label>
            <select
              id="open-checkin-location"
              value={locationId}
              onChange={(e: ChangeEvent<HTMLSelectElement>) => setLocationId(e.target.value)}
              className={inputClass}
              required
            >
              {locations.map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {loc.name}
                </option>
              ))}
              {session.locationId && !locations.some((loc) => loc.id === session.locationId) && (
                <option value={session.locationId}>{session.locationName ?? session.location}</option>
              )}
            </select>
            <p className="text-xs text-slate-400 mt-1">Required. Choose from the saved locations.</p>
          </div>

          <div>
            <label htmlFor="open-checkin-note" className="block text-sm font-medium text-navy mb-1">
              Note <span className="text-slate-400 font-normal">(optional, max 1000 chars)</span>
            </label>
            <textarea
              id="open-checkin-note"
              rows={3}
              value={note}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setNote(e.target.value)}
              className={inputClass}
              maxLength={1000}
            />
            <p className="text-xs text-slate-400 mt-1 text-right">{note.length}/1000</p>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 border border-slate-150">
            <p className="text-sm font-medium text-navy mb-1">Join link preview</p>
            <a
              href={joinUrl}
              target="_blank"
              rel="noreferrer"
              className="text-sm text-primary break-all hover:underline"
            >
              {joinUrl}
            </a>
          </div>

          <div>
            <p className="text-sm font-medium text-navy mb-2">
              Attendees to notify ({includedAttendees.length}/{sendableAttendees.length})
            </p>
            {sendableAttendees.length === 0 ? (
              <p className="text-sm text-slate-500 bg-gray-50 rounded-lg p-4 border border-slate-150">
                No registered attendees yet. You can still open check-in so walk-ins can register via QR.
              </p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                {sendableAttendees.map((attendee) => {
                  const override = overrides[attendee.id] ?? { email: attendee.student.email ?? '', include: true }
                  const emailInvalid = override.include && (!override.email.trim() || !emailRegex.test(override.email.trim()))
                  return (
                    <div
                      key={attendee.id}
                      className="flex items-start gap-3 p-3 rounded-lg border border-slate-150 bg-white"
                    >
                      <input
                        type="checkbox"
                        checked={override.include}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => toggleAttendeeInclude(attendee.id, e.target.checked)}
                        className="mt-1 accent-primary"
                        aria-label={`Notify ${attendee.student.firstName} ${attendee.student.lastName}`}
                      />
                      <div className="flex-1 min-w-0 space-y-1">
                        <p className="text-sm font-medium text-navy truncate">
                          {attendee.student.firstName} {attendee.student.lastName}
                        </p>
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-slate-400 shrink-0" />
                          <input
                            type="email"
                            value={override.email}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => updateAttendeeEmail(attendee.id, e.target.value)}
                            disabled={!override.include}
                            className={inputClass}
                            placeholder="attendee@company.com"
                          />
                        </div>
                        {emailInvalid && (
                          <p className="text-xs text-red-600">Enter a valid email address.</p>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} disabled={sending}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSend || sending}>
              <Send className="w-4 h-4" />
              {sending
                ? 'Sending…'
                : sendableAttendees.length === 0
                  ? 'Open Check-in'
                  : `Send to ${includedAttendees.length}`}
            </Button>
          </div>
        </form>
      </CardContent>
      {ToastUI}
    </Modal>
  )
}
